import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import apiClient from '@/api/client'
import { getConfig } from '@/config'
import { clearAuthScopedCaches } from '@/composables/useAuthScopedCaches'
import { useCurrentUserStore } from '@/stores/currentUser.store'

export const useAuthStore = defineStore('auth', () => {
  const loading = ref(false)

  const currentUserStore = useCurrentUserStore()

  /** Authenticated when the current user is loaded (cookie is httpOnly, can't read it) */
  const isAuthenticated = computed(() => !!currentUserStore.authUser)

  /** Flat AuthUser for sidebar/navigation — delegates to currentUser store */
  const currentUser = computed(() => currentUserStore.authUser)

  async function fetchCurrentUser() {
    await currentUserStore.fetchCurrentUser()
  }

  /** Redirect to backend's Google OAuth login endpoint */
  function loginWithGoogle() {
    const apiBase = getConfig().apiBaseUrl
    window.location.href = `${apiBase}/auth/google/login`
  }

  /** Called from AuthCallbackView — cookie already set by backend redirect */
  async function handleCallback() {
    loading.value = true
    try {
      currentUserStore.reset()
      // Defensive: drop any per-user PWA cache bytes the previous
      // session may have left in Workbox before the new user is
      // committed. See `useAuthScopedCaches` for the bucket list.
      await clearAuthScopedCaches()
      await currentUserStore.fetchCurrentUser()
    } finally {
      loading.value = false
    }
  }

  async function hijack(targetUserId: number) {
    await apiClient.post(`/auth-user/hijack/${targetUserId}`)
    currentUserStore.reset()
    await currentUserStore.fetchCurrentUser()
  }

  async function logout() {
    try {
      await apiClient.post('/auth/logout')
    } finally {
      // Clear per-user PWA cache bytes BEFORE dropping the local
      // user — anything else (router push, hijack rebound) is safe
      // to do once the buckets are empty.
      await clearAuthScopedCaches()
      currentUserStore.reset()
    }
  }

  /**
   * Exit an active hijack session.
   *
   * The hijack JWT carries `impersonated_by` claim and is short-lived (15 min).
   * Plan 1 ships only the entry path — exiting requires the admin to log out
   * (clearing the hijack cookie) and re-authenticate via Google. A dedicated
   * `/auth/hijack/exit` endpoint that re-issues the admin's token is a future
   * enhancement (carry-forward concern).
   */
  async function exitHijack() {
    await logout()
  }

  async function init() {
    try {
      await fetchCurrentUser()
    } catch {
      // No valid session — user stays null, will be redirected to login
    }
  }

  return {
    currentUser,
    loading,
    isAuthenticated,
    loginWithGoogle,
    handleCallback,
    fetchCurrentUser,
    hijack,
    exitHijack,
    logout,
    init,
  }
})
